import AxiosInstance from "@/config/AxiosInstance";
import { API_BASE_URL } from "@/config/appConfig";

type RRDRecord = Record<string, unknown>;

export type RRDDraftBulkLocationInfo = {
  sys_location_id?: string;
  fk_location_id?: number;
} & RRDRecord;

export type RRDDraftBulkSelection = {
  orderId?: string | null;
  printType?: string;
  mailingDate?: string | null;
  frontCouponExpiryDate?: string | null;
  backCouponExpiryDate?: string | null;
  differentCouponExpiry?: boolean;
  distribution?: boolean | number;
  estimatedDistribution?: number;
  distributionCost?: number;
  storeCopyCpm?: number;
  storeCopy?: boolean;
  storeCopyQnt?: number | string;
  storeCopyCost?: number;
  totalCost?: number;
  selectedAssets?: RRDRecord[] | RRDRecord;
  heroImages?: RRDRecord[];
  coupons?: RRDRecord[];
  locationInfo?: RRDDraftBulkLocationInfo;
} & RRDRecord;

export type RRDDraftBulkRequest = {
  selections: RRDDraftBulkSelection[];
  campaignId?: number | string;
  participantId?: number | string;
  sysParticipantId?: string;
  sysCampaignId?: string;
  brandId?: number | string;
  campaignName?: string;
  paymentMethod?: string;
  totalBudget?: number;
  userId?: number | string;
  adminCouponExpirationMode?: string;
  adminFixedCouponDurationDays?: number | null;
  adminMinimumCouponDurationDays?: number;
  adminCouponExpirationDate?: string | null;
  couponExpirationSetBy?: string;
  fixedCouponDurationDays?: number | null;
  removeOrderFromDraftOrderIds?: string[];
} & RRDRecord;

export type RRDBulkProcessingResult = {
  success?: Array<{
    location_id?: string;
    order_id?: string;
  }>;
  errors?: Array<{
    location_id?: string;
    order_id?: string;
    error?: string;
  }>;
};

export type RRDDraftBulkResponse = {
  success: boolean;
  message: string;
  results?: RRDBulkProcessingResult;
};

export type RRDGetDraftCampaignRequest = {
  campaignId: number | string;
  fkParticipantId?: number | string;
};

export type RRDDraftCampaignLocationInfo = {
  location_id?: string;
  sys_location_id?: string;
  fk_location_id?: number;
  id?: number;
  location_name?: string;
  street_address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  market_name?: string;
  distribution?: {
    enabled?: boolean;
    quantity?: number;
    cost?: number;
  };
  mailing_weeks?: string[];
} & RRDRecord;

export type RRDDraftCampaignSelection = {
  orderId?: string;
  locationInfo?: RRDDraftCampaignLocationInfo;
  selectedAssets?: {
    front?: {
      hero?: RRDRecord;
      coupons?: RRDRecord[];
    };
    back?: {
      hero?: RRDRecord;
      coupons?: RRDRecord[];
    };
  };
  locationId?: number;
  locationName?: string;
  heroImages?: string | RRDRecord[];
  coupons?: string | RRDRecord[];
  distribution?: boolean;
  estimatedDistribution?: number;
  distributionCost?: number;
  storeCopy?: boolean;
  storeCopyQnt?: number;
  storeCopyCost?: number;
  storeCopyCpm?: number;
  mailingDate?: string;
  differentCouponExpiry?: boolean;
  frontCouponExpiryDate?: string;
  backCouponExpiryDate?: string;
  totalCost?: number;
  printType?: string;
} & RRDRecord;

export type RRDDraftCampaignLocationResponse = {
  selections?: RRDDraftCampaignSelection[];
  locationData?: Array<{ locationName?: string } & RRDRecord>;
  orderIds?: string[];
  adminCouponExpirationMode?: string;
  adminFixedCouponDurationDays?: number | null;
  adminMinimumCouponDurationDays?: number;
  adminCouponExpirationDate?: string;
  couponExpirationSetBy?: string;
  fixedCouponDurationDays?: number | null;
  brandId?: number;
  userId?: number;
  participantId?: number;
  sysParticipantId?: string;
  campaignName?: string;
  paymentMethod?: string;
  totalBudget?: number;
  printType?: string;
  campaignId?: number | string;
} & RRDRecord;

export type RRDConfirmDraftBulkRequest = {
  orderIds: string[];
  updates?: Record<string, RRDRecord>;
} & RRDRecord;

export type RRDConfirmDraftBulkResponse = {
  status: number;
  success: boolean;
  message: string;
  results?: {
    success?: Array<{
      order_id?: string;
      composite?: RRDRecord;
    }>;
    errors?: Array<{
      order_id?: string;
      error?: string;
    }>;
  };
};

export const rrdCampaignService = {
  draftRRDCampaign,
  getDraftRRDCampaign,
  confirmRRDCampaignJoin,
};

function draftRRDCampaign(data: RRDDraftBulkRequest) {
  return AxiosInstance.post(
    `${API_BASE_URL}/postcard/rrd/orders/draft/bulk`,
    data
  );
}

function getDraftRRDCampaign({
  campaignId,
  fkParticipantId,
}: RRDGetDraftCampaignRequest) {
  return AxiosInstance.get(
    `${API_BASE_URL}/postcard/rrd/campaigns/${campaignId}/draftsCampaignLocation`,
    {
      params: {
        fkParticipantId,
      },
    }
  );
}

function confirmRRDCampaignJoin(data: RRDConfirmDraftBulkRequest) {
  return AxiosInstance.post(
    `${API_BASE_URL}/postcard/rrd/orders/draft/confirm/bulk`,
    data
  );
}
